/* eslint-disable indent */
/* eslint-disable @typescript-eslint/camelcase */
import React from 'react';
import {
  Checkbox,
  Grid,
  makeStyles,
  TextField,
  Typography,
} from '@material-ui/core';
import ApplicationState from 'store/interfaces';
import { connect } from 'react-redux';
import TableItems, { TableCell, TableRow } from 'components/TableItems';
import {
  BulkClientsFields,
  EnvironmentFields,
  ListBulkClientsProps,
} from 'modules/environments/interfaces';
import i18n from 'i18n';
import Button from 'components/Button';
import { modalClose, modalOpen } from 'modules/modal/actions';
import {
  editBulkClientsRequest,
  getBulkClientsRequest,
} from 'modules/environments/actions';
import { useTableFilters } from 'hooks/useTableFilters';
import TitleDivider from 'components/TitleDivider';
import { Form, Formik } from 'formik';
import DynamicTextField from 'components/DynamicTextField';
import { ModalFields } from 'modules/modal/interfaces';
import clsx from 'clsx';
import { Alert } from '@material-ui/lab';
import { SearchBar } from 'components/SearchBar';
import VerificationCode from 'components/VerificationCode';
import styles from '../styles';
import { initialQuery } from '..';
import schema from '../schema';
import screenFields from '../screenFields';

const useStyles = makeStyles(styles);

export interface LimitLicensesConfigProps {
  bulkClients: ListBulkClientsProps;
  isLoadingBulkClients: boolean;
  environment: EnvironmentFields | null;
  verificationCodeRef: React.MutableRefObject<string[]>;
  setVerificationCode: React.Dispatch<React.SetStateAction<string[]>>;
  modalOpen: (props: ModalFields) => void;
  modalClose: () => void;
  getBulkClientsRequest: (props) => void;
  editBulkClientsRequest: (props) => void;
}

const LimitLicensesConfig = ({
  bulkClients,
  isLoadingBulkClients,
  environment,
  verificationCodeRef,
  setVerificationCode,
  modalOpen,
  modalClose,
  getBulkClientsRequest,
  editBulkClientsRequest,
}: LimitLicensesConfigProps) => {
  const classes = useStyles();

  interface FormattedObject {
    cli_uuids: string[];
    license_limit_configuration: {
      has_license_limit: boolean;
      license_limit: number;
    };
  }

  const [checks, setChecks] = React.useState<BulkClientsFields[]>(
    bulkClients.results.filter(
      item => item.license_limit_configuration.has_license_limit === true
    )
  );

  const {
    query,
    searchValue,
    itemOrder,
    handleOrder,
    handleLimitChange,
    handlePageChange,
    handleSearch,
    setSearchValue,
  } = useTableFilters(initialQuery);

  const [availableLicenses, setAvailableLicenses] = React.useState(
    bulkClients?.results[0]?.license_limit_configuration.available_licenses
  );
  const totalLicensesInPage = bulkClients?.results?.reduce((acc, item) => {
    if (item?.license_limit_configuration?.has_license_limit) {
      return acc + item?.license_limit_configuration?.license_limit;
    }
    return acc;
  }, 0);
  const totalAvailableLicenses =
    (bulkClients?.results[0]?.license_limit_configuration.available_licenses ??
      0) + totalLicensesInPage;

  const getBulkClients = () => {
    getBulkClientsRequest({
      id: environment?.environment_uuid,
      query: { ...query },
    });
  };

  const enabledEnum = {
    false: i18n.t('NO_KEY'),
    true: i18n.t('YES_KEY'),
  };

  React.useEffect(() => {
    getBulkClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getBulkClientsRequest, query]);

  React.useEffect(() => {
    setChecks(
      bulkClients.results.filter(
        item => item.license_limit_configuration.has_license_limit
      )
    );
  }, [bulkClients]);

  React.useEffect(() => {
    const newTotalAvailableLicenses = checks?.reduce((acc, item) => {
      if (item?.license_limit_configuration?.has_license_limit) {
        return acc + item?.license_limit_configuration?.license_limit;
      }
      return acc;
    }, 0);
    setAvailableLicenses(totalAvailableLicenses - newTotalAvailableLicenses);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checks]);

  const handleCheck = (uuid: string) => {
    setChecks(prevChecks => {
      const existingItem = prevChecks.find(item => item.client_uuid === uuid);

      if (existingItem) {
        return prevChecks.map(item =>
          item.client_uuid === uuid
            ? {
                ...item,
                license_limit_configuration: {
                  ...item.license_limit_configuration,
                  has_license_limit: !item.license_limit_configuration
                    .has_license_limit,
                },
              }
            : item
        );
      }
      const newItem = bulkClients.results.find(
        item => item.client_uuid === uuid
      );

      if (newItem) {
        const updatedNewItem = {
          ...newItem,
          license_limit_configuration: {
            ...newItem.license_limit_configuration,
            has_license_limit: true,
          },
        };
        return [...prevChecks, updatedNewItem];
      }

      return prevChecks;
    });
  };

  const handleLicenseLimitChange = (uuid: string, value: number) => {
    // eslint-disable-next-line no-restricted-globals
    const isValueNaN = isNaN(value) ? 0 : value;
    setChecks(prevChecks =>
      prevChecks.map(item =>
        item.client_uuid === uuid
          ? {
              ...item,
              license_limit_configuration: {
                ...item.license_limit_configuration,
                license_limit: isValueNaN,
              },
            }
          : item
      )
    );
  };

  const headers = [
    {
      key: 'nome',
      i18n: 'CLIENT_NAME_KEY',
      order: true,
    },
    {
      key: 'has_license_limit',
      i18n: 'BULK_CLIENTS.ACTIVE_KEY',
      order: true,
      centerTitle: true,
    },
    {
      key: 'license_limit',
      i18n: 'BULK_CLIENTS.LICENSES_QUANTITY_KEY',
      centerTitle: true,
    },
  ];

  const formatObjects = (client: BulkClientsFields[]): FormattedObject[] => {
    return client.map(obj => ({
      cli_uuids: [obj.client_uuid],
      license_limit_configuration: {
        has_license_limit: obj.license_limit_configuration.has_license_limit,
        license_limit: obj.license_limit_configuration.license_limit,
      },
    }));
  };

  const formattedArray = formatObjects(checks);
  const handleConfirm = (values: any) => {
    const verificationCodeToSend = verificationCodeRef.current;
    editBulkClientsRequest({
      id: environment?.environment_uuid,
      dataValues: {
        operator_data: {
          action_reason: values?.action_reason,
          restricted_action_code: verificationCodeToSend.join(''),
        },
        clients_data: formattedArray,
      },
    });
    modalClose();
  };

  const handleSubmit = (values: any) => {
    setVerificationCode(Array(6).fill(''));
    editBulkClientsRequest({
      id: environment?.environment_uuid,
      dataValues: {
        operator_data: {
          action_reason: values?.action_reason,
        },
        clients_data: formattedArray,
      },
    });
    modalOpen({
      title: i18n.t('VERIFICATION_CODE_KEY'),
      content: (
        <VerificationCode
          length={6}
          setVerificationCode={setVerificationCode}
          onRetry={() => {
            editBulkClientsRequest({
              id: environment?.environment_uuid,
              dataValues: {
                operator_data: {
                  action_reason: values?.action_reason,
                },
                clients_data: formattedArray,
              },
            });
          }}
          onConfirm={() => {
            handleConfirm(values);
          }}
        />
      ),
      fullWidth: true,
    });
  };

  return (
    <Grid container spacing={2} className={classes.container}>
      <Grid container className={classes.valuesFormLicenses} spacing={2}>
        <Grid
          item
          className={clsx(classes.optionsCard, {
            [classes.valuesFormLicensesError]: availableLicenses < 0,
          })}
        >
          <Typography className={classes.activeAllTextLicenses}>
            {`${i18n.t('LICENSES_AVAILABLE_KEY')}:`}
          </Typography>
          <TextField
            id="available-licenses"
            variant="outlined"
            disabled={true}
            fullWidth
            value={availableLicenses}
            InputProps={{
              readOnly: true,
            }}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item className={classes.optionsCard}>
          <Typography className={classes.activeAllTextLicenses}>
            {`${i18n.t('BULK_CLIENTS.ENVIRONMENTS_TOTAL_LICENSES_KEY')}:`}
          </Typography>
          <TextField
            id="available-licenses"
            variant="outlined"
            disabled={true}
            fullWidth
            value={environment?.licenses.license_limit}
            InputProps={{
              readOnly: true,
            }}
          />
        </Grid>
      </Grid>
      {availableLicenses < 0 && (
        <Grid container justify="center" className={classes.alert}>
          <Alert severity="warning" color="error">
            <span>{i18n.t('AVAILABLE_LICENSES_REACHED_KEY')}</span>
          </Alert>
        </Grid>
      )}
      <Grid container>
        <SearchBar
          id="search-client"
          isLoading={isLoadingBulkClients}
          searchValue={searchValue}
          handleSearch={handleSearch}
          setSearchValue={setSearchValue}
        />
        <TableItems
          header={
            <>
              {headers.map(item => (
                <TableCell
                  key={item?.key}
                  order={item?.order}
                  centerTitle={item?.centerTitle}
                  handleOrder={() =>
                    item?.order ? handleOrder(item?.key) : null
                  }
                  markOderItem={itemOrder === item?.key}
                >
                  {i18n.t(item?.i18n)}
                </TableCell>
              ))}
            </>
          }
          renderItems={(item: BulkClientsFields, index) => {
            const checkItem = checks.find(
              check => check.client_uuid === item.client_uuid
            );
            const licenseLimitValue = checkItem
              ? checkItem.license_limit_configuration.license_limit
              : item.license_limit_configuration.license_limit;
            return (
              <TableRow key={index}>
                <TableCell>{item?.name}</TableCell>
                <TableCell textAlignCenter centerTitle>
                  <Checkbox
                    className={classes.checkbox}
                    onChange={() => {
                      handleCheck(item.client_uuid);
                    }}
                    checked={
                      !!checks.find(
                        check =>
                          check.client_uuid === item.client_uuid &&
                          check.license_limit_configuration
                            .has_license_limit === true
                      )
                    }
                  />
                </TableCell>
                <TableCell textAlignCenter centerTitle>
                  {checks.find(
                    check =>
                      check.client_uuid === item.client_uuid &&
                      check.license_limit_configuration.has_license_limit ===
                        true
                  ) ? (
                    <Grid item className={classes.cellContent}>
                      <TextField
                        id="edit-cell"
                        variant="outlined"
                        type="number"
                        onKeyDown={evt =>
                          ['e', 'E', '+', '-'].includes(evt.key) &&
                          evt.preventDefault()
                        }
                        onBlur={e =>
                          handleLicenseLimitChange(
                            item.client_uuid,
                            parseInt(e.target.value, 10)
                          )
                        }
                        onChange={e =>
                          handleLicenseLimitChange(
                            item.client_uuid,
                            parseInt(e.target.value, 10)
                          )
                        }
                        defaultValue={licenseLimitValue}
                        InputProps={{
                          inputProps: { min: '0', step: '1' },
                        }}
                      />
                    </Grid>
                  ) : (
                    <Typography className={classes.limitLicenseText}>
                      {item?.license_limit_configuration.license_limit}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            );
          }}
          overrideRowsCount={10}
          isLoading={isLoadingBulkClients}
          data={bulkClients}
          onChangeLimit={handleLimitChange}
          onChangePage={handlePageChange}
          notFoundText={i18n.t('NO_CLIENT_KEY')}
        />
      </Grid>
      <Grid container justify="flex-end" className={classes.buttons}>
        <Button
          id="activate-button"
          disabled={availableLicenses < 0}
          submitButton
          onClick={() => {
            modalOpen({
              title: i18n.t('BULK_CLIENTS.CONFIRM_ACTIVATE_KEY'),
              content: (
                <>
                  <>
                    <Grid container justify="center">
                      <Typography style={{ fontSize: 12, marginBottom: 12 }}>
                        {i18n.t('BULK_CLIENTS.CONFIRM_ACTIVATE_IN_CLIENTS_KEY')}
                      </Typography>
                    </Grid>
                    <TableItems
                      header={
                        <>
                          <TableCell className={classes.tableRow}>
                            {i18n.t('CLIENT_NAME_KEY')}
                          </TableCell>
                          <TableCell
                            textAlignCenter
                            centerTitle
                            className={classes.tableRow}
                          >
                            {i18n.t('BULK_CLIENTS.ACTIVE_KEY')}
                          </TableCell>
                          <TableCell
                            textAlignCenter
                            centerTitle
                            className={classes.tableRow}
                          >
                            {i18n.t('BULK_CLIENTS.LICENSES_QUANTITY_KEY')}
                          </TableCell>
                        </>
                      }
                      renderItems={(item: BulkClientsFields, index) => (
                        <TableRow key={index}>
                          <TableCell>{item?.name}</TableCell>
                          <TableCell textAlignCenter centerTitle>
                            {
                              enabledEnum[
                                String(
                                  item?.license_limit_configuration
                                    .has_license_limit
                                )
                              ]
                            }
                          </TableCell>
                          <TableCell textAlignCenter centerTitle>
                            {item?.license_limit_configuration.has_license_limit
                              ? item?.license_limit_configuration.license_limit
                              : '-'}
                          </TableCell>
                        </TableRow>
                      )}
                      isLoading={isLoadingBulkClients}
                      data={{
                        count: 0,
                        next: '',
                        previous: '',
                        results: checks,
                      }}
                      onChangeLimit={handleLimitChange}
                      onChangePage={handlePageChange}
                      notFoundText={i18n.t('NO_CLIENT_KEY')}
                      notFooter
                      smallLoading={true}
                    />
                  </>
                  <TitleDivider
                    overrideClasses={{
                      title: { margin: '12px 4px 8px 4px', fontSize: 12 },
                    }}
                  >
                    {i18n.t('BULK_CLIENTS.SET_CONNECTION_TYPE_TO_CONFIRM_KEY')}
                  </TitleDivider>
                  <Formik
                    initialValues={{ action_reason: '' }}
                    enableReinitialize
                    validationSchema={schema}
                    validateOnChange
                    validateOnBlur
                    onSubmit={handleSubmit}
                  >
                    {({ setFieldValue, errors, values, submitForm }) => {
                      return (
                        <React.Fragment>
                          <Form>
                            <Grid container>
                              <Grid
                                container
                                spacing={2}
                                className={classes.fields}
                              >
                                <DynamicTextField
                                  {...screenFields[0]}
                                  setFieldValue={setFieldValue}
                                  value={values?.action_reason}
                                  errors={errors}
                                />
                              </Grid>
                            </Grid>
                            <Grid
                              container
                              justify="flex-end"
                              style={{ marginTop: 12 }}
                            >
                              <Button
                                submitButton
                                id="confirm-button"
                                onClick={submitForm}
                                isLoading={isLoadingBulkClients}
                              >
                                {i18n.t('CONTINUE_KEY')}
                              </Button>
                              <Button
                                id="cancel-button"
                                variant="outlined"
                                onClick={modalClose}
                              >
                                {i18n.t('CANCEL_KEY')}
                              </Button>
                            </Grid>
                          </Form>
                        </React.Fragment>
                      );
                    }}
                  </Formik>
                </>
              ),
            });
          }}
        >
          {i18n.t('NEXT_KEY')}
        </Button>
        <Button id="cancel-button" variant="outlined" onClick={modalClose}>
          {i18n.t('CLOSE_KEY')}
        </Button>
      </Grid>
    </Grid>
  );
};

const mapStateToProps = ({
  authenticate: { data },
  environments: { bulkClients, isLoadingBulkClients },
}: ApplicationState) => ({
  role: data?.role,
  bulkClients,
  isLoadingBulkClients,
});

export default connect(mapStateToProps, {
  modalOpen,
  modalClose,
  getBulkClientsRequest,
  editBulkClientsRequest,
})(LimitLicensesConfig);
